import type { SessionUser } from "@eduleo/shared";
import type { QueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../stores/auth-store";

type SessionMessage = { type: "signed_out" } | { type: "signed_in"; key: string };

const CHANNEL = "eduleo-session";

/** Identifies who the cached data belongs to: institution + user. */
export function sessionKey(user: SessionUser | null): string | null {
  return user ? `${user.institution.code}/${user.id}` : null;
}

/** For query keys: data cached for one person can never be read under another's key. */
export function useSessionKey(): string | null {
  return useAuthStore((state) => sessionKey(state.user));
}

/**
 * Ties cached data to the person signed in:
 * - the query cache is emptied whenever the signed-in person changes or signs out, so the
 *   next person on a shared staff-room computer never sees the previous person's data;
 * - other open tabs are told, and sign out too when the account they show is gone.
 * Returns a cleanup function.
 */
export function bindSessionToCache(queryClient: QueryClient): () => void {
  const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
  let applyingRemote = false;

  const unsubscribe = useAuthStore.subscribe((state, previous) => {
    const after = sessionKey(state.user);
    if (after === sessionKey(previous.user)) return;
    queryClient.clear();
    if (applyingRemote || !channel) return;
    const message: SessionMessage = after
      ? { type: "signed_in", key: after }
      : { type: "signed_out" };
    channel.postMessage(message);
  });

  if (channel) {
    channel.onmessage = (event: MessageEvent<SessionMessage>) => {
      const current = sessionKey(useAuthStore.getState().user);
      if (!current) return;
      const message = event.data;
      const accountGone =
        message.type === "signed_out" || (message.type === "signed_in" && message.key !== current);
      if (!accountGone) return;
      applyingRemote = true;
      try {
        useAuthStore.getState().clearSession();
      } finally {
        applyingRemote = false;
      }
    };
  }

  return () => {
    unsubscribe();
    channel?.close();
  };
}
